import type { ApiResponse } from '@/types/apiResponse';
import type {
  AcademyCertificateItem,
  CertificateVerificationResult,
} from '@/types/academy/certificate';
import type { CertificateVisibility } from '@/types/academy/certificate';
import type {
  AcademyAutoApplyOffer,
  AcademyCartPreviewResult,
  AcademyCheckoutResult,
  AcademyEnrollmentItem,
  AcademyLandingResult,
  AcademyModuleDetailResult,
  AcademyResult,
} from '@/types/academy/academy';
import { apiService } from '../api';

/** Builds the cart request body, omitting `offerCode` entirely when no (non-blank) code is provided. */
function buildCartRequestBody(
  moduleIds: string[],
  offerCode?: string | null,
): { moduleIds: string[]; offerCode?: string } {
  const normalized = offerCode?.trim();
  return normalized ? { moduleIds, offerCode: normalized } : { moduleIds };
}

class AcademyService {
  private static instance: AcademyService;

  private constructor() {}

  public static getInstance(): AcademyService {
    if (!AcademyService.instance) {
      AcademyService.instance = new AcademyService();
    }
    return AcademyService.instance;
  }

  /**
   * Fetches all public academy data (tiers, modules, stats)
   * in a single request from GET /public/api/v1/academy.
   *
   * The result shape (AcademyResult) is extensible — new sections are added
   * as optional keys without changing this method or the endpoint URL.
   */
  async getAcademy(): Promise<ApiResponse<AcademyResult>> {
    return apiService.get<ApiResponse<AcademyResult>>('/public/api/v1/academy');
  }

  /**
   * Fetches the same academy data as `getAcademy` plus the signed-in user's
   * enrolled-module preview (first 3) and total enrollment count, so the
   * landing page does not need a separate enrollments request.
   * GET /private/api/v1/academy
   */
  async getAcademyWithEnrollments(): Promise<
    ApiResponse<AcademyLandingResult>
  > {
    return apiService.get<ApiResponse<AcademyLandingResult>>(
      '/private/api/v1/academy',
    );
  }

  /**
   * Verifies a certificate code. Public and unauthenticated, so a recruiter can check a credential
   * without an Espotz account.
   *
   * A 200 carries the outcome in `status` (VALID / EXPIRED / REVOKED / NOT_PUBLIC); an unknown,
   * malformed or soft-deleted code answers 404, which callers translate to the NOT_FOUND state.
   * GET /public/api/v1/academy/certificates/:certificateCode
   */
  async verifyCertificate(
    certificateCode: string,
  ): Promise<ApiResponse<CertificateVerificationResult>> {
    return apiService.get<ApiResponse<CertificateVerificationResult>>(
      `/public/api/v1/academy/certificates/${encodeURIComponent(certificateCode)}`,
    );
  }

  /**
   * Publishes or un-publishes one of the caller's certificates.
   *
   * PRIVATE makes the public verification page stop showing the learner's details — the code still
   * resolves, so a genuine credential never reads as forged. Owner-only; someone else's id answers
   * 404 rather than 403, so an id cannot be probed for existence.
   * PATCH /private/api/v1/academy/certificates/:certificateId/visibility
   */
  async updateCertificateVisibility(
    certificateId: string,
    visibility: CertificateVisibility,
  ): Promise<ApiResponse<AcademyCertificateItem>> {
    return apiService.patch<
      ApiResponse<AcademyCertificateItem>,
      { visibility: CertificateVisibility }
    >(
      `/private/api/v1/academy/certificates/${encodeURIComponent(certificateId)}/visibility`,
      { visibility },
    );
  }

  /**
   * Fetches full detail for a single ACTIVE module by its ID.
   * Used by the module detail page: GET /public/api/v1/academy/modules/:moduleId
   */
  async getAcademyModuleById(
    moduleId: string,
  ): Promise<ApiResponse<AcademyModuleDetailResult>> {
    return apiService.get<ApiResponse<AcademyModuleDetailResult>>(
      `/public/api/v1/academy/modules/${encodeURIComponent(moduleId)}`,
    );
  }

  /**
   * Fetches module detail for an enrolled user, including deleted/inactive modules.
   * GET /private/api/v1/academy/modules/:moduleId
   */
  async getEnrolledModuleById(
    moduleId: string,
  ): Promise<ApiResponse<AcademyModuleDetailResult>> {
    return apiService.get<ApiResponse<AcademyModuleDetailResult>>(
      `/private/api/v1/academy/modules/${encodeURIComponent(moduleId)}`,
    );
  }

  /**
   * Mints an auto-login redirect URL to open the module's Coursebox course.
   * GET /private/api/v1/academy/modules/:moduleId/coursebox-access
   */
  async getModuleCourseboxAccess(
    moduleId: string,
  ): Promise<ApiResponse<{ redirectUrl: string }>> {
    return apiService.get<ApiResponse<{ redirectUrl: string }>>(
      `/private/api/v1/academy/modules/${encodeURIComponent(moduleId)}/coursebox-access`,
    );
  }

  /**
   * POST /private/api/v1/academy/modules/:moduleId/claim
   * Provisions + enrolls a pre-registered learner on the Coursebox course once it's available.
   */
  async claimModuleCoursebox(
    moduleId: string,
  ): Promise<ApiResponse<{ provisioned: boolean }>> {
    return apiService.post<ApiResponse<{ provisioned: boolean }>>(
      `/private/api/v1/academy/modules/${encodeURIComponent(moduleId)}/claim`,
      {},
    );
  }

  /**
   * Lists active auto-apply offers eligible for the public academy cart.
   * GET /public/api/v1/academy/offers/auto-apply
   */
  async getAutoApplyOffers(): Promise<ApiResponse<AcademyAutoApplyOffer[]>> {
    return apiService.get<ApiResponse<AcademyAutoApplyOffer[]>>(
      '/public/api/v1/academy/offers/auto-apply',
    );
  }

  /**
   * Computes cart totals and the best eligible auto-apply offer using server-side module prices.
   * POST /public/api/v1/academy/cart/preview
   */
  async previewCart(
    moduleIds: string[],
    offerCode?: string | null,
  ): Promise<ApiResponse<AcademyCartPreviewResult>> {
    return apiService.post<ApiResponse<AcademyCartPreviewResult>>(
      '/public/api/v1/academy/cart/preview',
      buildCartRequestBody(moduleIds, offerCode),
    );
  }

  async previewCartAuthenticated(
    moduleIds: string[],
    offerCode?: string | null,
  ): Promise<ApiResponse<AcademyCartPreviewResult>> {
    return apiService.post<ApiResponse<AcademyCartPreviewResult>>(
      '/private/api/v1/academy/cart/preview',
      buildCartRequestBody(moduleIds, offerCode),
    );
  }

  async checkoutCart(
    moduleIds: string[],
    offerCode?: string | null,
  ): Promise<ApiResponse<AcademyCheckoutResult>> {
    return apiService.post<ApiResponse<AcademyCheckoutResult>>(
      '/private/api/v1/academy/cart/checkout',
      buildCartRequestBody(moduleIds, offerCode),
    );
  }

  async getEnrollments(): Promise<ApiResponse<AcademyEnrollmentItem[]>> {
    return apiService.get<ApiResponse<AcademyEnrollmentItem[]>>(
      '/private/api/v1/academy/enrollments',
    );
  }
}

export const academyService = AcademyService.getInstance();
